import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useForm, useFormState } from 'react-final-form';
import { ThreeDots } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import passwordGenerator from '../../../../utils/passwordGeneration';
import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import WithError from '../../../../components/with-error/WithError';
import { Input } from '../../../../components/common-inputs';
import PasswordInput from '../../../../components/common-inputs/PasswordInput';
import CommonButton from '../../../../components/common-button/CommonButton';
import Icon from '../../../../icons/Icon';
import { ICONS } from '../../../../icons';
import CommonCheckbox from '../../../../components/common-checkbox/CommonCheckbox';
import ProjectsSelect from '../../../../components/projects-select/ProjectsSelect';
import selectElementInArray from '../../../../utils/selectElementInArray';

import scss from './UserFormModal.module.scss';

const UserFormInner = ({ onSubmit, onCancel, submitButtonText, formTitle }) => {
  const { t } = useTranslation('customers');
  const { change } = useForm();
  const { submitting } = useFormState();

  const companyProjects = useSelector((state) => state.customers.customerInfo.companyProjects) || [];

  const formattedCompanyProjects = companyProjects.map(({ projectId, projectName, enabled }) => ({
    id: projectId,
    name: projectName,
    enabled,
  }));

  const handleGeneratePassword = useCallback(() => {
    change('password', passwordGenerator({ length: 20 }));
  }, [change]);

  const handleChangeAssignedProjects = ({ id, checked, projects }) => {
    change('assignedProjects', selectElementInArray(projects, id, checked));
  };

  return (
    <form className={scss.form} onSubmit={onSubmit}>
      <h3 className={scss.title}>{formTitle}</h3>

      <div className={scss.fieldsWrapper}>
        <Field name='firstName'>
          {({ input, meta }) => (
            <WithLabel label={t('userName')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.commonField}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input size='sm' formProps={input} isError={meta.touched && meta.error} disabled={submitting} />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='lastName'>
          {({ input, meta }) => (
            <WithLabel label={t('surname')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.commonField}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input size='sm' formProps={input} isError={meta.touched && meta.error} disabled={submitting} />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='username'>
          {({ input, meta }) => (
            <WithLabel label={t('email')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.commonField}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input size='sm' formProps={input} isError={meta.touched && meta.error} disabled={submitting} />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='password'>
          {({ input, meta }) => (
            <WithLabel label={t('password')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.passwordField}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <PasswordInput size='sm' formProps={input} disabled={submitting} isError={meta.touched && meta.error} />

                <CommonButton
                  styleColor='secondary'
                  maxWidth={110}
                  onClick={handleGeneratePassword}
                  disabled={submitting}
                  className={scss.generatePasswordButton}>
                  <Icon icon={ICONS.Lock} />
                  {t('generate')}
                </CommonButton>
              </WithError>
            </WithLabel>
          )}
        </Field>

        {/* <div className={scss.checkboxesRow}>
        <Field name='enabled' type='checkbox'>
          {({ input }) => (
            <WithLabel label={t('enabled')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.checkboxField}>
              <CommonCheckbox size='md' checked={input.checked} onChange={input.onChange} disabled={submitting} />
            </WithLabel>
          )}
        </Field>

        <Field name='isAdmin' type='checkbox'>
          {({ input }) => (
            <WithLabel label={t('companyAdmin')} variant={LABEL_STYLES.CUSTOM} extraClassName={scss.checkboxField}>
              <CommonCheckbox size='md' checked={input.checked} onChange={input.onChange} disabled={submitting} />
            </WithLabel>
          )}
        </Field>
      </div> */}

        <Field name='assignedProjects'>
          {({ input }) => (
            <WithLabel label={t('assignProjects')} variant={LABEL_STYLES.COLUMN}>
              <ProjectsSelect
                projectList={formattedCompanyProjects}
                activeProjects={input.value}
                onSelect={({ id, checked }) => handleChangeAssignedProjects({ id, checked, projects: input.value })}
              />
            </WithLabel>
          )}
        </Field>
      </div>

      <div className={scss.actions}>
        <CommonButton type='reset' styleColor='secondary' maxWidth={120} onClick={onCancel} disabled={submitting}>
          {t('cancel')}
        </CommonButton>

        <CommonButton type='submit' onClick={onSubmit} maxWidth={140} isLoading={submitting}>
          {submitButtonText}
        </CommonButton>
      </div>
    </form>
  );
};

export default UserFormInner;
